import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { AllowTemplates } from 'common/data/Permissions/UserMenuAccess';
import { NameIdObj } from 'common/models/GenericTypes';
import { ChangeTracker } from 'common/models/hooks/ChangeTracker';
import { InterviewNoteTemplate } from 'common/models/Templates/InterviewNoteTemplate';
import UserPicker from 'components/Pickers/UserPicker';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import TitleAndActionSummaryBar from 'components/SummaryBars/TitleAndActionSummaryBar';
import TextAreaWithFormat from 'components/TextAreaWithFormat';
import useObjectStateWithChangeTracker from 'hooks/UseObjectStateWithChangeTracker';
import useUnsavedChangesDialog from 'hooks/UseUnsavedChangesDialog';
import PageContentLayout from 'layouts/PageContentLayout';
import PageLayout from 'layouts/PageLayout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  CreateInterviewNoteTemplate,
  GetInterviewNoteTemplateById,
  UpdateInterviewNoteTemplate,
} from 'services/TemplatesService';

const templatesListLink = (
  <Link
    to="/account/templates/interview-notes"
    style={{ color: 'inherit', textDecoration: 'underline' }}
  >
    Interview Notes
  </Link>
);

interface InterviewNoteUi {
  id: number;
  name: string;
  ownerId: number;
  statusId: number;
  text: string;
}

const defaultTemplate: InterviewNoteUi = {
  id: 0,
  name: '',
  ownerId: 0,
  statusId: 1,
  text: '',
};

const noChangesDefault: ChangeTracker<InterviewNoteUi> = {
  id: false,
  ownerId: false,
  name: false,
  statusId: false,
  text: false,
};

const companyOwnedUserPickerOption: NameIdObj[] = [{ id: 0, name: 'Company Owned' }];

export default function InterviewNoteTemplateItemPage() {
  const [summaryBar, setSummaryBar] = useState<JSX.Element>(<></>);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { state, init, change, updateInitial, hasChanges } =
    useObjectStateWithChangeTracker<InterviewNoteUi>(defaultTemplate, noChangesDefault);

  const params = useParams();
  const itemId = useMemo(() => {
    const parsedId = +(params.id ?? '0');
    if (isNaN(parsedId)) return 0;
    return parsedId;
  }, [params.id]);

  const isTemplateAdmin = useMemo(() => AllowTemplates(), []);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      const data = await GetInterviewNoteTemplateById(itemId, setErrorMessage);
      if (data) {
        init({
          id: data.id,
          name: data.name,
          ownerId: data.ownerID,
          statusId: data.statusID,
          text: data.text,
        });
      }
      setIsLoading(false);
    };
    itemId !== 0 && getData();
  }, [itemId, init]);

  const saveHandler = useCallback(async () => {
    const trimmedName = state.name.trim();
    if (trimmedName === '') {
      setErrorMessage("The name field can't be empty");
      return false;
    }

    setIsLoading(true);
    let res: boolean | null = null;
    const data: InterviewNoteTemplate = {
      id: itemId,
      name: state.name,
      owner: '',
      ownerID: state.ownerId,
      status: '',
      statusID: state.statusId,
      text: state.text,
    };

    if (itemId !== 0) {
      res = await UpdateInterviewNoteTemplate(itemId, data, setErrorMessage);
    } else {
      res = await CreateInterviewNoteTemplate(data, setErrorMessage);
    }

    if (!res) {
      setIsLoading(false);
      return false;
    }
    updateInitial();
    setIsLoading(false);
    setShowSuccess(true);
    return true;
  }, [itemId, state, updateInitial]);

  useEffect(() => {
    const action = (
      <Button variant="contained" color="success" disabled={!hasChanges} onClick={saveHandler}>
        Save
      </Button>
    );

    const sb = (
      <TitleAndActionSummaryBar
        title={
          <>
            {'Account > Templates > '} &nbsp; {templatesListLink}
          </>
        }
        browserTabTitle="Interview Notes > Templates"
        action={action}
      />
    );
    setSummaryBar(sb);
  }, [hasChanges, saveHandler]);

  const { unsavedChangesDialog, hasBlockedRoute } = useUnsavedChangesDialog(
    hasChanges,
    saveHandler
  );

  return (
    <PageLayout SummaryBar={summaryBar}>
      {unsavedChangesDialog}
      {!hasBlockedRoute && showSuccess && itemId === 0 && (
        <Navigate to="/account/templates/interview-notes" />
      )}
      <Snackbar open={showSuccess} autoHideDuration={3000} onClose={() => setShowSuccess(false)}>
        <Alert onClose={() => setShowSuccess(false)}>Changes Saved</Alert>
      </Snackbar>
      <Snackbar open={errorMessage !== ''} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <PageContentLayout showLoading={isLoading}>
        <Stack spacing={2}>
          <RWTextFieldComponent
            label="Name"
            value={state.name}
            onChange={(e) => change('name', e.target.value)}
          />
          {isTemplateAdmin && (
            <UserPicker
              onSelect={(u) => u && change('ownerId', u.id)}
              userId={state.ownerId}
              label="Template Owner"
              appendToStart={companyOwnedUserPickerOption}
            />
          )}
          <TextField
            select
            label="Status"
            value={state.statusId.toString()}
            onChange={({ target }) => change('statusId', +target.value)}
          >
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="2">Inactive</MenuItem>
          </TextField>
          <TextAreaWithFormat
            initialValue={state.text}
            onContentChange={(c) => change('text', c)}
          />
        </Stack>
      </PageContentLayout>
    </PageLayout>
  );
}
